import { AlertCircle, Info, RefreshCw, ShieldAlert, TriangleAlert } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, ContractViolationError } from '@/lib/api/http';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Code } from './primitives';

/**
 * Loading, empty, and error states.
 *
 * The brief bans placeholder boxes, and these three components are how that is kept true without
 * every screen re-inventing them. In particular {@link ErrorState} refuses to render "Something
 * went wrong": it reads the SAD §8 envelope and shows the operator the real code, the real
 * message, and the `request_id` they will be asked for in an incident channel.
 */

/** A shimmering placeholder shaped like the content it stands in for. */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div className={cn('skeleton-sheen rounded-md', className)} aria-hidden="true" {...props} />
  );
}

/** A skeleton table with `rows` rows and `columns` columns. */
export function TableSkeleton({
  rows = 6,
  columns = 5,
}: {
  /** Number of skeleton rows. */
  rows?: number | undefined;
  /** Number of skeleton columns. */
  columns?: number | undefined;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="space-y-2" role="status" aria-label={t('common.loading')}>
      <span className="sr-only">{t('common.loading')}</span>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 rounded-md border border-border/60 p-3">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton
              key={c}
              className={cn('h-4', c === 0 ? 'w-32' : c === columns - 1 ? 'w-16' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Props for {@link EmptyState}. */
export interface EmptyStateProps {
  /** Icon shown above the title. */
  icon?: ReactNode | undefined;
  /** Short headline. */
  title: string;
  /** One or two sentences explaining what would put something here. */
  description: string;
  /** Optional call to action. */
  action?: ReactNode | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/**
 * An empty state that explains *why* it is empty.
 *
 * "No results" tells an operator nothing. "No cases are open — verification auto-approved every
 * slip in the last 24 hours" tells them the system is healthy, which is a different message
 * entirely from "your filter excluded everything".
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): ReactNode {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-grid px-6 py-14 text-center',
        className,
      )}
    >
      <div className="flex size-11 items-center justify-center rounded-full border border-border bg-background text-muted-foreground">
        {icon ?? <Info className="size-5" aria-hidden="true" />}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      {action}
    </div>
  );
}

/** Props for {@link ErrorState}. */
export interface ErrorStateProps {
  /** The thrown error. */
  error: unknown;
  /** Retry handler; a button appears when provided and the error is retryable. */
  onRetry?: () => void;
  /** Additional classes. */
  className?: string | undefined;
}

/** Render an error with everything the operator needs to act on or report it. */
export function ErrorState({ error, onRetry, className }: ErrorStateProps): ReactNode {
  const { t } = useTranslation();
  const api = error instanceof ApiError ? error : null;
  const contract = error instanceof ContractViolationError ? error : null;
  const message =
    api?.message ??
    contract?.message ??
    (error instanceof Error ? error.message : t('common.requestFailed'));

  const title = api?.isForbidden
    ? t('common.noPermissionTitle')
    : api?.isUnauthenticated
      ? t('common.sessionEndedTitle')
      : api?.isConflict
        ? t('common.noLongerPossibleTitle')
        : contract
          ? t('common.unexpectedShapeTitle')
          : t('common.somethingFailedTitle');

  const Icon = api?.isForbidden ? ShieldAlert : contract ? TriangleAlert : AlertCircle;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-5',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="break-words text-sm leading-relaxed text-muted-foreground">{message}</p>
          {api ? (
            <p className="flex flex-wrap items-center gap-2 pt-1 text-2xs text-muted-foreground">
              <Code>{api.code}</Code>
              <span aria-hidden="true">·</span>
              <span>HTTP {api.status}</span>
              {api.requestId ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    request <Code>{api.requestId}</Code>
                  </span>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      </div>
      {onRetry && (api === null || api.retryable || api.status >= 500) ? (
        <div>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw aria-hidden="true" />
            {t('common.tryAgain')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Props for {@link Callout}. */
export interface CalloutProps {
  /** Visual weight. */
  tone?: 'info' | 'warning' | 'danger' | 'success' | undefined;
  /** Headline. */
  title?: string | undefined;
  /** Body. */
  children: ReactNode;
  /** Additional classes. */
  className?: string | undefined;
}

/** An inline note explaining a rule or a consequence. */
export function Callout({ tone = 'info', title, children, className }: CalloutProps): ReactNode {
  const tones = {
    info: 'border-state-flight/30 bg-state-flight-bg text-state-flight',
    warning: 'border-state-attention/30 bg-state-attention-bg text-state-attention',
    danger: 'border-state-bad/30 bg-state-bad-bg text-state-bad',
    success: 'border-state-good/30 bg-state-good-bg text-state-good',
  } as const;
  const icons = {
    info: Info,
    warning: TriangleAlert,
    danger: AlertCircle,
    success: Info,
  } as const;
  const Icon = icons[tone];
  return (
    <div className={cn('flex gap-3 rounded-lg border p-3.5 text-sm', tones[tone], className)}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-1 leading-relaxed">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className="text-foreground/80">{children}</div>
      </div>
    </div>
  );
}
